/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `SideMenuBottom` for the Caintra Connect frontend.
 * @module components/SideMenu/SideMenuBottom
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { SvgIcon } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styles from "../../styles/SideMenu.module.css";
import { ReactComponent as LogoutIcon } from "../../assets/icons/Logout.svg";
import { ReactComponent as HelpIcon } from "../../assets/icons/SideMenu/HelpIcon.svg";
import { ReactComponent as ChatIcon } from "../../assets/icons/SideMenu/ChatIcon.svg";
import { ROUTES } from "./constants";
import { canAccessChat } from "../../utils/accessControl";

/** Bottom navigation links: help, chat, and logout. */
const SideMenuBottom = ({
  user,
  permissions,
  isCollapsed,
  isMobile,
  isMenuItemActive,
  navigateTo,
  onSignOut,
}) => {
  const { t } = useTranslation();

  const menuItemClass = (path, context) =>
    `${styles.menuItem} ${
      isMenuItemActive(path, context) ? styles.active : ""
    }`;

  return (
    <div className={styles.bottomMenuOptions}>
      <div
        className={menuItemClass(ROUTES.HELP)}
        onClick={() => navigateTo(ROUTES.HELP)}
      >
        <HelpIcon className={styles.menuIcon} />
        <span className={styles.menuText}>{t("pages.menu.help")}</span>
      </div>

      {canAccessChat(user, permissions) && (
        <div
          className={menuItemClass(ROUTES.CHAT)}
          onClick={() => navigateTo(ROUTES.CHAT)}
        >
          <ChatIcon className={styles.menuIcon} />
          <span className={styles.menuText}>{t("pages.menu.chat")}</span>
        </div>
      )}

      <div className={styles.logoutButton} onClick={onSignOut}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "10px",
          }}
        >
          <SvgIcon
            className={styles.logoutIcon}
            sx={{ fontSize: "18px" }}
            component={LogoutIcon}
          />
        </div>
        {(!isCollapsed || isMobile) && <span>{t("pages.menu.logout")}</span>}
      </div>
    </div>
  );
};

/** Legal footer with copyright and policy links. */
export const SideMenuFooter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={styles.footerContainer}>
      <span>CAINTRA Connect &copy; 2025</span>
      <span
        className={styles.footerLink}
        onClick={() => navigate(ROUTES.TERMS_AND_CONDITIONS)}
      >
        {t("pages.menu.termsAndConditions")}
      </span>
      <span
        className={styles.footerLink}
        onClick={() => navigate(ROUTES.PRIVACY_POLICY)}
      >
        {t("pages.menu.privacyPolicy")}
      </span>
    </div>
  );
};

export default SideMenuBottom;
